import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import CTAButton from "@/components/CTAButton";
import { fadeUp, viewport, stagger } from "@/lib/motion";
import { FlaskConical, ShieldCheck, UserCog, Target } from "lucide-react";

const PILLARS = [
  { icon: ShieldCheck, label: "Evidence-based" },
  { icon: UserCog, label: "Personalized" },
  { icon: Target, label: "Outcome-focused" },
];

export const About = () => {
  return (
    <section id="about" className="bg-sa-bg py-12 md:py-16" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-20">
          <Reveal className="flex flex-col lg:col-span-7">
            <div>
              <p className="mb-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
                About SuperAge Health
              </p>
              <h2 className="font-display text-4xl font-medium leading-[1.08] tracking-tight text-sa-forest sm:text-5xl">
                Physician-led precision metabolic liver health.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-sa-muted">
                SuperAge Health™ is a physician-led precision metabolic liver health platform
                helping people understand, protect, and optimize their liver health
                before disease develops. We combine evidence-based digital
                assessments, precision diagnostics, and personalized medical
                guidance to identify risks early and empower informed health
                decisions. Starting with liver health—the body's metabolic
                powerhouse—our vision is to redefine the future of preventive and
                precision organ health.
              </p>
            </div>

            <div className="mt-auto flex flex-col items-start gap-5 pt-10 sm:flex-row sm:items-center">
              <CTAButton testId="about-start-assessment">Know Your Liver in 2 Minutes</CTAButton>
              <p className="max-w-xs border-l-2 border-sa-copper pl-4 text-sm leading-relaxed text-sa-muted">
                A free 2-minute digital assessment. No clinic visit to begin.
              </p>
            </div>
          </Reveal>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="flex flex-col gap-4 lg:col-span-5"
          >
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.label}
                  variants={fadeUp}
                  className="flex items-center gap-4 rounded-[22px] border border-sa-beige bg-white px-6 py-5"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-sa-forest/5 text-sa-copper">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-display text-xl font-medium text-sa-forest">
                    {p.label}
                  </span>
                </motion.div>
              );
            })}

            <motion.div
              variants={fadeUp}
              className="mt-1 rounded-[22px] bg-sa-forest p-6"
            >
              <p className="text-[12px] font-semibold uppercase tracking-[0.25em] text-sa-copper">
                Brand philosophy
              </p>
              <p className="mt-2 font-display text-2xl font-medium text-white">
                Assess. Restore. Sustain.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
